﻿import React, { forwardRef, ReactElement, ReactNode, useImperativeHandle, useRef, useState } from 'react';

import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { Toast, ToastContainer } from 'react-bootstrap';
import { Variant } from 'react-bootstrap/esm/types';
import { useIntl } from 'react-intl';

import 'components/GMetrixToast/gmetrix-toast.scss';

interface ToastButton {
    iconClass?: string;
    onClick: () => void;
    text: string;
    variant?: 'primary' | 'secondary' | 'outline-primary' | 'outline-secondary' | 'success' | 'danger' | 'warning' | 'info';
}

const GMetrixToast = forwardRef((props, forwardRef) => {

    const { formatMessage } = useIntl();
    const [toastQueue, setToastQueue] = useState<ReactElement>();
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const createToast = (toastMessage: ReactNode, toastVariant: Variant, closeTimeout = 5000, button?: ToastButton) => {

        // Clear any existing timeout to prevent premature closure
        if (timeoutRef.current) {

            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;

        }

        if (toastMessage === undefined || toastMessage === null || toastMessage === '') {

            console.error('No message was provided for this toast');
            toastMessage = `GMetrix Error #001: No toast message was provided.`;
            toastVariant = 'danger';

        }
        const newToast = (
            <Toast
                aria-atomic="true"
                aria-live={toastVariant === 'danger' ? 'assertive' : 'polite'}
                bg={toastVariant}
                key={toastMessage.toString()}
                role="alert"
            >

                <Toast.Body className="d-flex justify-content-between align-items-center" style={{ color: toastVariant === 'danger' ? 'white' : '#0D2238' }}>
                    <span className="flex-grow-1">
                        {toastMessage}
                    </span>
                    <div className="d-flex align-items-center gap-2">
                        {button && (
                        <GMetrixButton
                            onClick={() => {

                                button.onClick();
                                if (timeoutRef.current) {

                                    clearTimeout(timeoutRef.current);
                                    timeoutRef.current = null;

                                }
                                setToastQueue(<></>);

                            }}
                            iconClass={button.iconClass}
                            size="sm"
                            variant={button.variant || 'outline-primary'}
                        >
                            {button.text}
                        </GMetrixButton>
                        )}
                        <button
                            onClick={() => {

                                if (timeoutRef.current) {

                                    clearTimeout(timeoutRef.current);
                                    timeoutRef.current = null;

                                }
                                setToastQueue(<></>);

                            }}
                            aria-label={formatMessage({ id: 'close.99e01fb5', defaultMessage: 'Close' })}
                            className="btn btn-link p-0 border-0"
                            style={{ color: 'inherit', cursor: 'pointer', lineHeight: 1 }}
                            type="button"
                        >
                            <i aria-hidden="true" className="fa-light fa-xmark fa-lg"></i>
                        </button>
                    </div>
                </Toast.Body>
            </Toast>
        );

        setToastQueue(newToast);

        // if closeTimeout is set it will auto close the message in specified amount of milliseconds
        if (closeTimeout && toastVariant !== 'danger') {

            timeoutRef.current = setTimeout(() => {

                setToastQueue(<></>);
                timeoutRef.current = null;

            }, closeTimeout);

        }

    };

    const clearAllToasts = () => {

        if (timeoutRef.current) {

            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;

        }
        setToastQueue(undefined);

    };

    useImperativeHandle(forwardRef, () => ({
        clearAllToasts: clearAllToasts,
        createToast: createToast
    }), []);

    return (
        <ToastContainer aria-live="polite" className="gmetrix-toast-container">
            {toastQueue}
        </ToastContainer>
    );

});

GMetrixToast.displayName = 'GMetrixToast';
export default GMetrixToast;